<template>
  <v-dialog
    v-model="isOpen"
    max-width="500"
    :aria-label="`Pick a meal for ${dayLabel}`"
  >
    <v-card>
      <v-card-title>{{ dayLabel }}</v-card-title>
      <v-card-text>
        <v-text-field
          v-model="search"
          label="Search recipes or type a custom meal"
          prepend-icon="mdi-magnify"
          variant="outlined"
          clearable
          autofocus
        />
        <v-list v-if="shownSuggestions.length || filteredRecipes.length">
          <template v-for="group in groups" :key="group.title">
            <v-list-subheader v-if="group.recipes.length">
              {{ group.title }}
            </v-list-subheader>
            <v-list-item
              v-for="recipe in group.recipes"
              :key="`${group.title}-${recipe.id}`"
              :title="recipe.title"
              :subtitle="recipe.subTitle"
              @click="selectRecipe(recipe)"
            >
              <template v-slot:prepend>
                <v-avatar>
                  <v-img v-if="recipe.image" :src="recipe.image" alt="" />
                  <v-icon v-else icon="mdi-food" />
                </v-avatar>
              </template>
            </v-list-item>
          </template>
        </v-list>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="close">Cancel</v-btn>
        <v-btn
          color="primary"
          variant="flat"
          :disabled="!search"
          @click="submitCustom"
        >
          Add custom meal
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
  import { computed, defineComponent, type PropType, ref, watch } from 'vue';
  import type { Recipe } from '@/service/recipe/types';
  import type { MealEntry } from '@/service/weekplan/types';

  export default defineComponent({
    name: 'MealPickerDialog',
    props: {
      open: {
        type: Boolean,
        default: false,
      },
      dayLabel: {
        type: String,
        required: true,
      },
      recipes: {
        type: Array as PropType<Recipe[]>,
        default: () => [],
      },
      // Shown above the recipes until a search is typed
      suggestions: {
        type: Array as PropType<Recipe[]>,
        default: () => [],
      },
    },
    emits: ['select', 'close'],
    setup(props, { emit }) {
      const isOpen = ref(props.open);
      const search = ref('');

      watch(
        () => props.open,
        (val) => {
          isOpen.value = val;
          if (val) search.value = '';
        },
      );
      watch(isOpen, (val) => {
        if (!val) emit('close');
      });

      const filteredRecipes = computed(() => {
        if (!search.value) return props.recipes;
        const term = search.value.toLowerCase();
        return props.recipes.filter(
          (r) =>
            r.title.toLowerCase().includes(term) ||
            r.subTitle.toLowerCase().includes(term),
        );
      });

      const shownSuggestions = computed(() =>
        search.value ? [] : props.suggestions,
      );

      const groups = computed(() => [
        { title: 'Suggestions', recipes: shownSuggestions.value },
        { title: 'Recipes', recipes: filteredRecipes.value },
      ]);

      function selectRecipe(recipe: Recipe) {
        const entry: MealEntry = { recipeId: recipe.id, title: recipe.title };
        emit('select', entry);
      }

      function submitCustom() {
        if (search.value) {
          const entry: MealEntry = { title: search.value };
          emit('select', entry);
        }
      }

      function close() {
        emit('close');
      }

      return {
        isOpen,
        search,
        filteredRecipes,
        shownSuggestions,
        groups,
        selectRecipe,
        submitCustom,
        close,
      };
    },
  });
</script>
